import { useCallback } from 'react';
import { gql, MutationResult, useApolloClient, useMutation } from '@apollo/client';
import { SESSION_TIMED_OUT, useAuthToken } from './useAuthToken';
import { useHistory, useLocation } from 'react-router-dom';
import { useAnalytics, Events, useQueryParams } from '@hooks';
import { useLessonFeedbackStatusStorage } from './useLessonFeedbackStatusStorage';
import { logger } from '../utils/logger';

export const loginMutationGQL = gql`
  mutation login($input: LoginInput!) {
    login(input: $input) {
      token, uri, sid, verified, email,
      id
    }
  }
`;

export type LoginResponse = {
  token?: string;
  uri?: string;
  sid?: string;
  verified?: string;
  email?: string;
  id?: string;
};

type UseLoginMutationResponse = [(email: string, password: string) => void, () => void, MutationResult];
export const useLoginMutation = (isParentApp?: boolean): UseLoginMutationResponse => {
  const { setAuthToken, removeAuthToken, removeHsaAuthToken, removeLockAccount, removeAuthPassword, removeVisitProfile, removeParentAuthToken, removeFirstResetLesson } = useAuthToken();
  const { removeFeedbackStatus } = useLessonFeedbackStatusStorage();
  const client = useApolloClient();
  const { push } = useHistory();
  const params = useQueryParams();
  const returnUrl = params.get('returnUrl');
  const { search } = useLocation();

  const { identify, trackEvent, segment } = useAnalytics();

  const [mutation, mutationResults] = useMutation<{ login: LoginResponse }>(loginMutationGQL, {
    onCompleted: (data) => {
      if (data.login.token) {
        removeAuthToken();
        removeFeedbackStatus();
        setAuthToken(data.login.token, true);
        
        const sessionTimedOut = localStorage.getItem(SESSION_TIMED_OUT);
        if (sessionTimedOut) {
          logger.debug('[Login] Clearing session timeout flag', {
            timeoutSetAt: new Date(Number(sessionTimedOut)).toISOString(),
            timeElapsed: Date.now() - Number(sessionTimedOut),
            timestamp: new Date().toISOString()
          });
        }
        localStorage.removeItem(SESSION_TIMED_OUT);
        if (data.login.id) {
          segment?.identify(data.login.id);
          identify(data.login.id, {
            email: data.login.email,
          });
        }
        const d = { email: data.login.email, loginType: 'Email', userId: data.login?.id };
        trackEvent(Events.actions.logIn, d);
        if (returnUrl && search.replace('?returnUrl=', '').includes(returnUrl)) {          
          const sharePath = search.replace('?returnUrl=', '').replace(`${returnUrl}&`, `${returnUrl}?`)
          push(sharePath);  
        } else {
          push('/');
        }
      } else if (data.login.sid) {
        const state: any = { totpId: data.login.sid, email: data.login.email }
        if (data.login.verified !== 'true' && data.login.uri) {
          state.totpUri = data.login.uri
        }
        push('/verify', state);
      } else if (data.login.email) {
        push('/admin/verify', { email: data.login.email });
      }
    },
    onError: (error, options) => {
      trackEvent(Events.actions.logInFailed, {email: options?.variables?.email, loginType: 'Email', clientId: options?.variables?.clientId, message: "Login attempt failed", error: error.message});
      logger.debug(`[Error]`, error);
    },
  });

  const login = useCallback((email: string, password: string) => {
    return mutation({
      variables: {
        input: {
          email,
          password,
          clientId: localStorage.getItem('clientId')
        }
      },
    });
  }, [mutation]);

  const logout = useCallback(async () => {
    await client.clearStore();
    trackEvent(Events.actions.logOut);
    removeAuthToken();
    removeHsaAuthToken();
    removeLockAccount();
    removeAuthPassword();
    removeVisitProfile();
    removeParentAuthToken();
    removeFirstResetLesson();
    if (isParentApp) {
      window.location.href = 'https://openparachuteschools.com/';
    } else {
      window.location.href = '/login';
    }
  }, [client, isParentApp, removeAuthPassword, removeAuthToken, removeHsaAuthToken, removeLockAccount, removeVisitProfile, removeParentAuthToken, trackEvent, removeFirstResetLesson]);

  return [login, logout, mutationResults];
}
